import { useState } from "react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const INPUT_CLASS = "w-full px-4 py-3 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 bg-white transition-colors";
const SUPPORT_EMAIL = "customersupport@driveclearapp.com";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent — our team will reply within 1 business day.");
    } catch (err) {
      toast.error("Could not send your message. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">Support &amp; Feedback</div>
            <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight mb-4">
              Questions about DriveClear?
            </h2>
            <p className="text-stone-600 text-sm leading-relaxed mb-8">
              Reach out with feature requests, calculator feedback, or general support inquiries.
            </p>
            <div className="flex items-center gap-3 text-sm text-stone-700">
              <span className="w-9 h-9 rounded-xl bg-[#faf9f7] border border-stone-200 flex items-center justify-center text-stone-900 font-bold">@</span>
              <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="contact-support-email" className="hover:text-blue-700 transition-colors">{SUPPORT_EMAIL}</a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-[#faf9f7] p-6 sm:p-8 rounded-3xl border border-stone-200">
              <h3 className="text-lg font-bold text-stone-900 mb-6">Send a Message to Support</h3>
              {sent && (
                <div data-testid="contact-success-message" className="mb-4 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                  Your message was sent. We respond within 1 business day.
                </div>
              )}
              <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-semibold text-stone-700 mb-1.5">Your Name</label>
                    <input id="contact-name" data-testid="contact-name-input" type="text" required placeholder="Jane Doe" value={form.name} onChange={set("name")} className={INPUT_CLASS} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-semibold text-stone-700 mb-1.5">Email Address</label>
                    <input id="contact-email" data-testid="contact-email-input" type="email" required placeholder="jane@example.com" value={form.email} onChange={set("email")} className={INPUT_CLASS} />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-xs font-semibold text-stone-700 mb-1.5">Subject</label>
                  <input id="contact-subject" data-testid="contact-subject-input" type="text" placeholder="Question about the Fit Check or early access" value={form.subject} onChange={set("subject")} className={INPUT_CLASS} />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-semibold text-stone-700 mb-1.5">Message</label>
                  <textarea id="contact-message" data-testid="contact-message-input" rows="4" required placeholder="How can we help you?" value={form.message} onChange={set("message")} className={INPUT_CLASS}></textarea>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-stone-500">We respond within 1 business day.</span>
                  <button type="submit" disabled={loading} data-testid="contact-submit-button" className="px-6 py-3 rounded-xl bg-stone-900 text-white font-semibold text-sm hover:bg-stone-800 disabled:opacity-60 transition-colors">
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
